import { useEffect, useState } from "react";
import {
  tipPaidEvent,
  tipUnits,
  type PublicTip,
  type PublicTipList,
} from "../../../shared/src/tips";
import "../../src/ui/tokens.css";
import "../../src/ui/supporters.css";

/**
 * What the editor's canvas and the template example show before a site has any tips. Their times
 * are counted when drawn: a Worker's clock reads 1970 while its modules load ("57 years ago").
 */
export const sampleSupporters = (now = Date.now()): PublicTipList => ({
  supporters: 128,
  items: [
    {
      id: "sample-1",
      name: "Maya",
      units: 3,
      unit: "coffee",
      plural: "",
      emoji: "☕",
      message: "Your last video got me through finals week. Thank you!",
      paidAt: new Date(now - 2 * 3_600_000).toISOString(),
    },
    {
      id: "sample-2",
      name: "Someone",
      units: 1,
      unit: "coffee",
      plural: "",
      emoji: "☕",
      message: "",
      paidAt: new Date(now - 26 * 3_600_000).toISOString(),
    },
    {
      id: "sample-3",
      name: "Jordan K.",
      units: 5,
      unit: "coffee",
      plural: "",
      emoji: "☕",
      message: "Keep making things. This is the best corner of the internet.",
      paidAt: new Date(now - 5 * 86_400_000).toISOString(),
    },
  ],
});

const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
function ago(iso: string) {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, size] of steps)
    if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  return "just now";
}

/**
 * The website's public tips, newest first: who bought how many of the creator's "coffees" and
 * what they wrote. The list loads after the page does (api/src/tips/routes.ts /wall), so a
 * prepared page never holds a stale one; private and hidden tips never reach it.
 */
export function Supporters({
  heading,
  limit,
  sample = false,
}: {
  heading: string;
  limit: number;
  /** The editor's canvas and template previews: example supporters instead of real ones. */
  sample?: boolean;
}) {
  const [list, setList] = useState<PublicTipList | null>(() =>
    sample ? sampleSupporters() : null,
  );
  // Bumped when a Tip box on the page hears its tip went through, to read the list again fresh.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (sample) return;
    const refresh = () => setVersion((current) => current + 1);
    window.addEventListener(tipPaidEvent, refresh);
    return () => window.removeEventListener(tipPaidEvent, refresh);
  }, [sample]);
  useEffect(() => {
    if (sample) return;
    let stopped = false;
    void fetch(`/api/tips/wall?limit=${limit}`, version ? { cache: "reload" } : undefined)
      .then((response) => (response.ok ? (response.json() as Promise<PublicTipList>) : null))
      .then((result) => {
        if (!stopped) setList(result ?? { items: [], supporters: 0 });
      })
      .catch(() => undefined);
    return () => {
      stopped = true;
    };
  }, [limit, sample, version]);
  const items = list?.items.slice(0, limit) ?? [];
  return (
    <section className="ui-supporters" data-ui="supporters" aria-busy={list ? undefined : true}>
      {(heading || !!list?.supporters) && (
        <div className="ui-supporters-head">
          {heading && <h3 className="ui-supporters-heading">{heading}</h3>}
          {!!list?.supporters && (
            <p className="ui-supporters-count">
              {list.supporters === 1 ? "1 supporter" : `${list.supporters} supporters`}
            </p>
          )}
        </div>
      )}
      {list && !items.length ? (
        <p className="ui-supporters-empty">Be the first to support.</p>
      ) : (
        <ol className="ui-supporters-list">
          {items.map((tip) => (
            <SupporterRow key={tip.id} tip={tip} />
          ))}
        </ol>
      )}
    </section>
  );
}

function SupporterRow({ tip }: { tip: PublicTip }) {
  return (
    <li className="ui-supporter">
      <span className="ui-supporter-mark" aria-hidden="true">
        {tip.emoji || tip.name.charAt(0).toUpperCase()}
      </span>
      <div className="ui-supporter-body">
        <p className="ui-supporter-line">
          <strong>{tip.name}</strong> bought {tipUnits(tip, tip.units)}
          <time className="ui-supporter-time" dateTime={tip.paidAt}>
            {ago(tip.paidAt)}
          </time>
        </p>
        {tip.message && <p className="ui-supporter-message">{tip.message}</p>}
      </div>
    </li>
  );
}
